<script setup>
import {ref, reactive, computed, watch, onMounted, nextTick} from 'vue'
import {NodeSelection} from '@tiptap/pm/state'
import ImageSelection from '@/common/ImageSelection.vue'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    editor: {type: Object, required: true},
    formId: [Number, String],
    image: Object, // activeImageAttrs
})

const emit = defineEmits(['hide-modal'])

const appStore = useAppStore()

// --- State ---

const fileId = ref(0)
const imagePos = ref(null)
const style = ref('')
const lockRatio = ref(true)

const attrs = reactive({
    alt: '',
    title: '',
    width: '',
    height: '',
    sizes: '',
    src: ''
})

// --- Computed ---

const currentAlignment = computed(() => {
    const s = style.value.toLowerCase()
    if (s.includes('float: left') || s.includes('margin-right: auto; margin-left: 0')) return 'left'
    if (s.includes('margin-left: auto; margin-right: auto')) return 'center'
    if (s.includes('float: right') || s.includes('margin-left: auto; margin-right: 0')) return 'right'
    return 'none'
})

// --- Methods ---

/**
 * Directly manipulates the ProseMirror state to update node attributes
 */
const updateTipTapAttr = (newAttrs) => {
    if (imagePos.value === null) return

    const {state, view} = props.editor
    const node = state.doc.nodeAt(imagePos.value)

    if (!node || node.type.name !== 'image') return

    const tr = state.tr.setNodeMarkup(imagePos.value, null, {
        ...node.attrs,
        ...newAttrs,
    })

    // Re-select to prevent focus loss
    const selection = NodeSelection.create(tr.doc, imagePos.value)
    tr.setSelection(selection)
    tr.setMeta('scrollIntoView', false)

    view.dispatch(tr)
}

const onImageSelection = (item) => {
    fileId.value = item.id
    const newSrc = `/asset/file/${fileId.value}`
    attrs.src = newSrc

    if (props.image) {
        updateTipTapAttr({src: newSrc})
    } else {
        // Insert new image
        const {from} = props.editor.state.selection
        props.editor.chain().focus().setImage({src: newSrc}).run()
        imagePos.value = from

        nextTick(() => {
            const selection = NodeSelection.create(props.editor.state.doc, from)
            props.editor.view.dispatch(props.editor.state.tr.setSelection(selection))
        })
    }
    refreshDimensions()
}

const setAlignment = (side) => {
    let styleString = 'display: block; max-width: 100%; height: auto;'

    if (side === 'left') styleString += ' float: left; margin-right: 15px;'
    else if (side === 'center') styleString += ' margin-left: auto; margin-right: auto;'
    else if (side === 'right') styleString += ' float: right; margin-left: 15px;'

    style.value = side === 'none' ? '' : styleString
    updateTipTapAttr({style: style.value})
}

const getOriginalDimensions = () => {
    return new Promise((resolve) => {
        const img = new Image()
        const host = appStore.ccmsSchemeAndHost || ''
        const src = attrs.src || props.image?.src

        if (!src) return resolve({width: 0, height: 0})

        img.src = src.startsWith('http') ? src : host + src
        img.onload = () => resolve({width: img.width, height: img.height})
        img.onerror = () => resolve({width: 0, height: 0})
    })
}

const setWidth = async () => {
    if (lockRatio.value && attrs.width) {
        const orig = await getOriginalDimensions()
        if (orig.width) {
            attrs.height = Math.round(attrs.width * (orig.height / orig.width))
        }
    }
}

const setHeight = async () => {
    if (lockRatio.value && attrs.height) {
        const orig = await getOriginalDimensions()
        if (orig.height) {
            attrs.width = Math.round(attrs.height * (orig.width / orig.height))
        }
    }
}

const refreshDimensions = async () => {
    if (!attrs.src) return
    const dims = await getOriginalDimensions()
    attrs.width = dims.width
    attrs.height = dims.height
}

// --- Lifecycle & Watchers ---

onMounted(() => {
    // Detect if we are editing an existing image
    const {selection} = props.editor.state
    if (selection.node && selection.node.type.name === 'image') {
        imagePos.value = selection.from
    }

    if (props.image) {
        Object.assign(attrs, {
            alt: props.image.alt || '',
            title: props.image.title || '',
            width: props.image.width || '',
            height: props.image.height || '',
            sizes: props.image.sizes || null,
            src: props.image.src || ''
        })

        style.value = props.image.style ? props.image.style.toLowerCase() : ''

        if (attrs.src) {
            fileId.value = parseInt(attrs.src.split('/').pop())
            if (attrs.width === '') refreshDimensions()
        }
    }
})

// Watch for attribute changes to sync with Tiptap
watch(attrs, (newVal) => {
    updateTipTapAttr(newVal)
}, {deep: true})
</script>

<template>
    <div>
        <div class="modal-backdrop fade show"></div>
        <div
            class="modal fade show d-block"
            tabindex="-1"
            role="dialog"
            @click.self="$emit('hide-modal')"
        >
            <div class="modal-dialog modal-lg">
                <div class="modal-content shadow-lg">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            <i class="bi-image me-2"></i>Image Properties
                        </h4>
                        <button aria-label="Close" class="btn-close" type="button"
                                @click="$emit('hide-modal')"></button>
                    </div>

                    <div class="modal-body">
                        <form class="form-horizontal px-3">
                            <div class="mb-4 row align-items-center">
                                <label class="col-sm-4 col-lg-3 form-label fw-bold">Image</label>
                                <div class="col-sm-8 col-lg-9">
                                    <ImageSelection :file-id="fileId" :on-change="onImageSelection" :form-id="formId"/>
                                </div>
                            </div>

                            <div class="mb-4 row">
                                <label class="col-sm-4 col-lg-3 form-label" for="altText">Alt Text</label>
                                <div class="col-sm-8 col-lg-9">
                                    <input id="altText" v-model="attrs.alt" :disabled="!fileId" class="form-control"
                                           placeholder="">
                                </div>
                            </div>

                            <div class="mb-4 row">
                                <label class="col-sm-4 col-lg-3 form-label" for="title">Title</label>
                                <div class="col-sm-8 col-lg-9">
                                    <input id="title" v-model="attrs.title" :disabled="!fileId" class="form-control">
                                </div>
                            </div>

                            <div class="mb-4 row">
                                <label class="col-sm-4 col-lg-3 form-label">Alignment</label>
                                <div class="col-sm-8 col-lg-9">
                                    <div class="btn-group w-100">
                                        <button
                                            v-for="align in ['none', 'left', 'center', 'right']"
                                            :key="align"
                                            type="button"
                                            class="btn btn-outline-secondary px-3"
                                            :class="{ 'active': currentAlignment === align }"
                                            :disabled="!fileId"
                                            @click="setAlignment(align)"
                                        >
                                            <i v-if="align !== 'none'"
                                               :class="`bi-align-${align === 'left' ? 'start' : align === 'right' ? 'end' : 'center'}`"></i>
                                            <span v-else>None</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-4 row">
                                <label class="col-sm-4 col-lg-3 form-label" for="width">Dimensions</label>
                                <div class="col-sm-8 col-lg-9 d-flex align-items-center">
                                    <div class="input-group">
                                        <span class="input-group-text">W</span>
                                        <input id="width" v-model.number="attrs.width" :disabled="!fileId"
                                               @input="setWidth" class="form-control text-center" type="number">

                                        <span class="input-group-text">H</span>
                                        <input id="height" v-model.number="attrs.height" :disabled="!fileId"
                                               @input="setHeight" class="form-control text-center" type="number">
                                    </div>

                                    <button
                                        :disabled="!fileId"
                                        class="ms-2 btn btn-light border"
                                        type="button"
                                        title="Lock Aspect Ratio"
                                        :class="{ 'btn-warning border-warning': lockRatio }"
                                        @click="lockRatio = !lockRatio"
                                    >
                                        <i class="bi" :class="lockRatio ? 'bi-lock-fill' : 'bi-unlock'"/>
                                    </button>

                                    <button
                                        :disabled="!fileId"
                                        class="ms-1 btn btn-light border"
                                        type="button"
                                        title="Reset to Original"
                                        @click="refreshDimensions"
                                    >
                                        <i class="bi-arrow-clockwise"/>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>